import { Badge } from "@/components/ui/badge";
import type { Invoice, AdminInvoice } from "@/lib/api/invoices";

const CURRENCY_SYMBOL = "₦";

export { CURRENCY_SYMBOL };

export function InvoiceStatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === "paid") {
    return (
      <Badge className="border border-emerald-200 bg-emerald-50 font-medium capitalize text-slate-900 shadow-none hover:bg-emerald-50">
        Paid
      </Badge>
    );
  }
  if (s === "pending") {
    return (
      <Badge className="border border-amber-200 bg-amber-50 font-medium capitalize text-slate-900 shadow-none hover:bg-amber-50">
        Pending
      </Badge>
    );
  }
  if (s === "cancelled") {
    return (
      <Badge className="border border-slate-200 bg-slate-100 font-medium capitalize text-slate-900 shadow-none hover:bg-slate-100">
        Cancelled
      </Badge>
    );
  }
  return <Badge className="capitalize">{status}</Badge>;
}

export function invoiceTitle(inv: Invoice | AdminInvoice): string {
  return (
    ((inv.metadata as Record<string, unknown>)?.description as string) ||
    `Invoice #${inv.id.slice(-8)}`
  );
}

/** Library share when the invoice is split, otherwise the invoice total. */
export function tenantInvoiceAmount(inv: Pick<Invoice, "amountDue" | "schoolShare">): number {
  const schoolShare = inv.schoolShare;
  if (schoolShare != null && !Number.isNaN(Number(schoolShare)) && Number(schoolShare) > 0) {
    return Number(schoolShare);
  }
  return inv.amountDue;
}

/** Fine amount a patron sees. Uses the linked invoice's library share when one exists. */
export function patronFineAmount(
  fineAmount: string,
  invoice?: Pick<Invoice, "amountDue" | "schoolShare"> | null
): number {
  if (invoice) return tenantInvoiceAmount(invoice);
  const parsed = Number.parseFloat(fineAmount);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function patronLabel(inv: AdminInvoice): string {
  if (!inv.user) return "—";
  const u = inv.user as { firstName?: string; lastName?: string; email: string };
  const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
  return name ? `${name} (${u.email})` : u.email;
}

export function dateCell(inv: Invoice | AdminInvoice): string {
  if (inv.status === "paid" && inv.paidAt) {
    return new Date(inv.paidAt).toLocaleDateString();
  }
  if (inv.dueDate) {
    return `Due ${new Date(inv.dueDate).toLocaleDateString()}`;
  }
  return "—";
}
